// Pre-PO line-item shipping DATE validation — the pure, DB-free core of the
// SO→PO Round-1 "Editing shipping information" save gate (SO→PO rebuild T02).
//
// Shared on purpose (same discipline as po-preconditions / order-money):
//   - the WEB modal runs it live so the rep sees findings as they type, then the
//     save-gate groups them by document with Continue-editing / Save-with-errors
//     (soft block, explicit override — never a silent hard stop);
//   - the API service (services/line-item-shipping.ts) runs the SAME validators
//     server-side so the authoritative 422 (save_with_errors soft-block) and the
//     client preview can never drift;
//   - the offline QA gate EXECUTES it against fixture dates, one per finding class.
//
// Home note: T02 was scoped to apps/web, but the API service AND the offline gate
// must reuse these validators verbatim — and apps/web is not importable from
// apps/api — so this lands in packages/shared, exactly like derivePoPreconditions
// / linesMissingArtwork (pure logic shared by web + api + the gate).

/** The document each finding is attributed to on the save gate, commonsku-style
 *  ("Quote: …", "Supplier PO: …", "Decorator PO: …"). */
export type ShippingDocLabel = "Quote" | "Supplier PO" | "Decorator PO";

export type ShippingFindingSeverity = "error" | "warning";

/** Machine codes — stable identifiers the gate + UI key on (never the copy). */
export type ShippingFindingCode =
  | "in_hands_date_in_past"
  | "supplier_ship_date_after_in_hands"
  | "supplier_ship_date_today"
  | "decorator_ship_date_same_as_supplier";

export interface ShippingFinding {
  code: ShippingFindingCode;
  severity: ShippingFindingSeverity;
  document: ShippingDocLabel;
  message: string;
}

export interface LineShippingValidationInput {
  /** SO in-hands date (YYYY-MM-DD) — drives all downstream date validation. */
  inHandsDate?: string | null;
  /** Supplier PO leg required ship date (YYYY-MM-DD). */
  supplierShipDate?: string | null;
  /** ship_to_routing — the decorator leg is validated ONLY when "decorator". */
  routing?: string | null;
  /** Decorator PO leg ship date (YYYY-MM-DD). */
  decoratorShipDate?: string | null;
  /** Injectable "today" (YYYY-MM-DD) for deterministic gates; defaults to the
   *  real current UTC date when omitted. */
  today?: string | null;
}

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Parse a YYYY-MM-DD string to a UTC day-count for comparison, or null when it
 *  is missing / malformed / an impossible calendar date (2026-02-30). Mirrors
 *  po-shipping.ts::isShipDateSane's round-trip guard so nonsense never compares. */
function ymdToUtcDays(s: string | null | undefined): number | null {
  if (!s || !YMD_RE.test(s)) return null;
  const [y, m, d] = s.split("-").map(Number) as [number, number, number];
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (
    dt.getUTCFullYear() !== y ||
    dt.getUTCMonth() !== m - 1 ||
    dt.getUTCDate() !== d
  ) {
    return null;
  }
  return Math.floor(dt.getTime() / 86_400_000);
}

function todayUtcYmd(): string {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, "0");
  const d = String(now.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Validate the cross-document dates on one line's staged shipping row. Findings
 * are returned in document order (Quote → Supplier PO → Decorator PO) and
 * each is tagged by the document it belongs to. Every check needs a real,
 * parseable date on BOTH sides — a null/blank/malformed field produces no
 * finding (the row is a draft the rep fills progressively).
 */
export function validateLineItemShippingDates(
  input: LineShippingValidationInput,
): ShippingFinding[] {
  const findings: ShippingFinding[] = [];
  const todayDays = ymdToUtcDays(input.today ?? todayUtcYmd());
  const inHandsDays = ymdToUtcDays(input.inHandsDate);
  const supplierDays = ymdToUtcDays(input.supplierShipDate);

  // Quote — in-hands date in the past (ERROR).
  if (inHandsDays !== null && todayDays !== null && inHandsDays < todayDays) {
    findings.push({
      code: "in_hands_date_in_past",
      severity: "error",
      document: "Quote",
      message: "Quote in-hands date is in the past.",
    });
  }

  // Supplier PO — ship date after the in-hands date (ERROR): the goods can't
  // leave the supplier after the client needs them in hand.
  if (supplierDays !== null && inHandsDays !== null && supplierDays > inHandsDays) {
    findings.push({
      code: "supplier_ship_date_after_in_hands",
      severity: "error",
      document: "Supplier PO",
      message: "Supplier PO shipping date is after the Quote in-hands date.",
    });
  }

  // Supplier PO — ship date set to today (WARNING): valid but a same-day ship is
  // a scheduling risk worth flagging.
  if (supplierDays !== null && todayDays !== null && supplierDays === todayDays) {
    findings.push({
      code: "supplier_ship_date_today",
      severity: "warning",
      document: "Supplier PO",
      message: "Supplier PO shipping date is set to today.",
    });
  }

  // Decorator PO — ONLY when the supplier leg routes to a decorator. Same-as-
  // supplier-date (WARNING): the decorator can't ship finished goods the same
  // day the blanks are due to leave the supplier.
  if (input.routing === "decorator") {
    const decoratorDays = ymdToUtcDays(input.decoratorShipDate);
    if (decoratorDays !== null && supplierDays !== null && decoratorDays === supplierDays) {
      findings.push({
        code: "decorator_ship_date_same_as_supplier",
        severity: "warning",
        document: "Decorator PO",
        message: "Decorator PO shipping date is the same as the Supplier PO date.",
      });
    }
  }

  return findings;
}

export interface ShippingValidationResult {
  errors: ShippingFinding[];
  warnings: ShippingFinding[];
}

/** Split a flat finding list into the {errors, warnings} shape the service and
 *  the save gate both consume. */
export function splitShippingFindings(
  findings: ShippingFinding[],
): ShippingValidationResult {
  return {
    errors: findings.filter((f) => f.severity === "error"),
    warnings: findings.filter((f) => f.severity === "warning"),
  };
}

const DOC_ORDER: ShippingDocLabel[] = [
  "Quote",
  "Supplier PO",
  "Decorator PO",
];

export interface ShippingFindingGroup {
  document: ShippingDocLabel;
  findings: ShippingFinding[];
}

/** Group findings by document label (in stable document order), dropping any
 *  document with no findings — the shape the save-gate renders. */
export function groupShippingFindingsByDocument(
  findings: ShippingFinding[],
): ShippingFindingGroup[] {
  return DOC_ORDER.map((document) => ({
    document,
    findings: findings.filter((f) => f.document === document),
  })).filter((g) => g.findings.length > 0);
}
